import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import "./InfiniteMarquee.css";
import { marqueeItems } from "../../../config/constants";
import MarqueeCard from "../../../theme/marqueeCard/MarqueeCard";
import ImageRandomizer from "../../../utils/imageRandomizer/ImageRandomizer";

function useIsReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
}

/**
 * Infinitely scrolling marquee with variable-width marqueeItems and fixed height.
 * Renders two identical groups and animates the track from 0 to -50% (or back).
 */
export default function InfiniteMarquee({
  gap = 12,
  speed = 48, // px/sec
  direction = "left", // "left" | "right"
  pauseOnHover = true,
  gradient = true,
  gradientWidth = 32,
  className = "",
}) {
  const containerRef = useRef(null);
  const groupRef = useRef(null);
  const [duration, setDuration] = useState(20);
  const [paused, setPaused] = useState(false);
  const reducedMotion = useIsReducedMotion();

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([entry]) =>
      setPaused(!entry.isIntersecting),
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Recalculate duration based on one group's measured width
  useLayoutEffect(() => {
    const el = groupRef.current;
    if (!el) return;

    const compute = () => {
      const rect = el.getBoundingClientRect();
      const groupWidth = rect.width + gap || 0;
      const d = groupWidth > 0 && speed > 0 ? groupWidth / speed : 20;
      setDuration(Math.max(1, d));
      // 👇 inject CSS var for distance
      el.parentElement?.style.setProperty(
        "--marquee-shift",
        `-${groupWidth}px`,
      );
    };

    compute();

    let ro = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => compute());
      ro.observe(el);
    }

    const onResize = () => compute();
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      if (ro) ro.disconnect();
    };
  }, [speed, marqueeItems.length, gap]);

  const decoratedItems = useMemo(
    () =>
      marqueeItems.map((item) => ({
        text: item,
        image: ImageRandomizer(),
      })),
    [marqueeItems], // regenerate only if the list of cards changes
  );

  const style = {
    "--marquee-gap": `${gap}px`,
    "--marquee-duration": `${duration}s`,
    "--marquee-gradient": gradient ? `${gradientWidth}px` : "0px",
  };

  const containerCls = [
    "im-marquee",
    direction === "right" ? "is-right" : "is-left",
    paused && pauseOnHover ? "is-paused" : "",
    gradient ? "with-gradient" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      ref={containerRef}
      className={containerCls}
      style={style}
      onMouseEnter={() => pauseOnHover && setPaused(true)}
      onMouseLeave={() => pauseOnHover && setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      aria-label="Scrolling content"
      aria-live="off"
    >
      <div className="im-track" data-reduced={reducedMotion ? "true" : "false"}>
        <div className="im-group" ref={groupRef} aria-hidden="false">
          {decoratedItems.map((child, i) => (
            <React.Fragment key={`a-${i}`}>
              <div className="im-item">
                <img
                  src={child.image}
                  alt="decorator"
                  style={{ height: "100%", objectFit: "contain" }}
                />
              </div>
              <div className="im-item">
                <MarqueeCard marqueeItem={child.text} />
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* duplicate for seamless loop */}
        <div className="im-group" aria-hidden="true">
          {decoratedItems.map((child, i) => (
            <React.Fragment key={`b-${i}`}>
              <div className="im-item">
                <img
                  src={child.image}
                  alt="decorator"
                  style={{ height: "100%", objectFit: "contain" }}
                />
              </div>
              <div className="im-item">
                <MarqueeCard marqueeItem={child.text} />
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}
